import React, { useCallback, useMemo, useState } from 'react';
import {
  SyncLayersRequest,
  LayerResponseDtoLimitTypeEnum,
} from 'libs/pinnacle-sdk';
import 'styles/ag-grid.css';
import { LayerStructure, LayerStructureTableProps } from './types';
import { convertLayerStructureToPayload, validateLayer } from './forms';
import FormActionButtons from 'components/ui/form-action-buttons';
import { layerApi, priceRatingGatewayUrl } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { handleError } from 'utils/form';
import { debounce, isNil, round } from 'lodash';
import {
  AllRowsData,
  ECellValueDisplayMode,
  ECellValueType,
  EExcelTableColumnType,
  EExcelTablePaginationType,
  ExcelTableColDef,
  RowData,
} from 'components/ui/excel-table/types';
import ExcelTable from 'components/ui/excel-table';
import { ADD_ROW_KEY } from 'components/ui/excel-table/constants';
import useSocketIO, { ESocketEvents } from 'hooks/useWebSocket';
import { IPriceRatingUpdatedMessage } from 'services/apis/types';
import { isSubmissionEditable } from 'utils/submission';
import { EditableCallbackParams } from 'ag-grid-community';

export const LayerStructureTable: React.FC<LayerStructureTableProps> = ({
  selectedCurrency,
  submission,
  includeBack,
  includeNext,
  navigateBack,
  navigateNext,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [hasError, setHasError] = useState({
    strict: false,
    soft: false,
  });
  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const { data: layersData, isLoading } = useQuery(
    [EQueryKeys.FILTER_LAYERS, submission.submissionId, selectedCurrency],
    () =>
      layerApi.filterLayers({
        filterLayersQueryDto: {
          submissionId: submission.submissionId,
          includeRateAdequacy: true,
          currency: selectedCurrency,
        },
      }),
    {
      enabled: !!submission.submissionId,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const { sendMessage } = useSocketIO<IPriceRatingUpdatedMessage>({
    url: priceRatingGatewayUrl,
    requireAuth: true,
    onConnect: () => {
      sendMessage(ESocketEvents.SUBSCRIBE, {
        submissionId: submission.submissionId,
      });
    },
    registerEvents: [
      {
        event: ESocketEvents.PRICE_RATING_UPDATED,
        onMessage: (message) => {
          if (message.submissionId === submission.submissionId) {
            queryClient.invalidateQueries([
              EQueryKeys.FILTER_LAYERS,
              submission.submissionId,
              selectedCurrency,
            ]);
          }
        },
      },
    ],
  });

  const RequiredHeader = (text: string) => (
    <div className="whitespace-normal">
      {text}
      <span className="text-red-500"> *</span>
    </div>
  );

  const rowData = useMemo(() => {
    if (!layersData) return [];

    return layersData.items.map((layer) => ({
      layerNumber: layer.layerNumber,
      currency: selectedCurrency,
      limitType: layer.limitType,
      limit: round(Number(layer.limit), 0),
      excess: !isNil(layer.excess) ? round(Number(layer.excess), 0) : undefined,
      deductibles: !isNil(layer.deductibles)
        ? round(Number(layer.deductibles), 0)
        : undefined,
      layerPremium: round(Number(layer.premium), 0),
      totalDeductions: Number(layer.totalDeductions),
      lineSize: Number(layer.lineSize),
      shareOfLimitQuoted: 0,
      rateAdequacy: layer.rateAdequacy ?? '0',
      layerId: layer.layerId,
    }));
  }, [layersData, selectedCurrency]);

  const createLayersMutation = useMutation(
    (createLayersRequest: SyncLayersRequest) =>
      layerApi.syncLayers(createLayersRequest),
    {
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('layerStructure.createdFailed'),
        }),
    }
  );

  const syncLayerStructureData = useCallback(
    async (layerStructures: LayerStructure[]) => {
      const currentLayerStructureData = {
        submissionId: submission.submissionId,
        syncLayerPayloadDto: convertLayerStructureToPayload(layerStructures),
      };

      await createLayersMutation.mutateAsync(currentLayerStructureData);
      queryClient.invalidateQueries([
        EQueryKeys.SUB_LIMIT_SETTINGS,
        submission.submissionId,
      ]);
    },
    [createLayersMutation, queryClient, submission.submissionId]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSyncLayersMutate = useCallback(
    debounce((layerStructures: LayerStructure[]) => {
      syncLayerStructureData(layerStructures);
    }, 500),
    [createLayersMutation]
  );

  const isCurrencyMismatch = useMemo(
    () =>
      submission.originalCurrency &&
      submission.originalCurrency !== selectedCurrency,
    [submission.originalCurrency, selectedCurrency]
  );

  const isEditableColum = useCallback(
    (params: EditableCallbackParams) => {
      return (
        isEditingMode &&
        !isCurrencyMismatch &&
        params.data.layerNumber !== ADD_ROW_KEY
      );
    },
    [isCurrencyMismatch, isEditingMode]
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'currency',
        headerName: '',
        headerComponent: () =>
          RequiredHeader(t('layerStructure.headers.currency')),
        minWidth: 108,
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        field: 'limitType',
        headerName: '',
        headerComponent: () =>
          RequiredHeader(t('layerStructure.headers.limitType')),
        minWidth: 190,
        flex: 1,
        resizable: true,
        editable: isEditableColum,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: Object.values(LayerResponseDtoLimitTypeEnum).map(
          (value) => ({
            value,
            label: value,
          })
        ),
      },
      {
        field: 'limit',
        headerName: '',
        resizable: true,
        headerComponent: () =>
          RequiredHeader(t('layerStructure.headers.limit')),
        minWidth: 150,
        flex: 1,
        editable: isEditableColum,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'excess',
        headerName: t('layerStructure.headers.excess'),
        minWidth: 170,
        flex: 1,
        resizable: true,
        editable: isEditableColum,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'deductibles',
        headerName: t('layerStructure.headers.deductibles'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: isEditableColum,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'layerPremium',
        headerName: '',
        resizable: true,
        headerComponent: () =>
          RequiredHeader(t('layerStructure.headers.layerPremium')),
        minWidth: 160,
        flex: 1,
        editable: isEditableColum,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'rateAdequacy',
        headerName: t('layerStructure.headers.rateAdequacy'),
        minWidth: 160,
        flex: 1,
        editable: false,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'totalDeductions',
        headerName: '',
        resizable: true,
        headerComponent: () =>
          RequiredHeader(t('layerStructure.headers.totalDeductions')),
        minWidth: 170,
        flex: 1,
        editable: isEditableColum,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'lineSize',
        headerName: '',
        resizable: true,
        headerComponent: () =>
          RequiredHeader(t('layerStructure.headers.lineSize')),
        minWidth: 125,
        flex: 1,
        editable: isEditableColum,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'shareOfLimitQuoted',
        headerName: t('layerStructure.headers.shareOfLimitQuoted'),
        minWidth: 160,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        valueGetter: (params: {
          data: { lineSize?: number; limit?: number };
        }) => {
          if (
            (params.data.lineSize || params.data.lineSize == 0) &&
            params.data.limit
          ) {
            return (params.data.lineSize * params.data.limit) / 100;
          }
          return '';
        },
      },
    ],
    [isEditableColum, t]
  );

  const handleNavigateBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const handleNavigateNext = useCallback(() => {
    navigateNext();
  }, [navigateNext]);

  const onDataChange = useCallback(
    (_: RowData<LayerStructure>, rows: AllRowsData<LayerStructure>) => {
      if (!rows.isError.strict) {
        debouncedSyncLayersMutate(rows.data);
      }
    },
    [debouncedSyncLayersMutate]
  );

  const onDeleteRow = useCallback(
    (_: RowData<LayerStructure>, rows: AllRowsData<LayerStructure>) => {
      if (!rows.isError.strict) {
        debouncedSyncLayersMutate(rows.data);
      }
    },
    [debouncedSyncLayersMutate]
  );

  return (
    <>
      <ExcelTable
        rowKeyColName="layerNumber"
        tableContainerClassName="h-[20rem]"
        loading={isLoading}
        columnDefs={columnDefs}
        data={rowData}
        tableClassName="rounded-t-lg"
        className="mb-2"
        paginationType={EExcelTablePaginationType.INFO}
        includeIdRow={true}
        includeAddRow={isEditingMode && !isCurrencyMismatch}
        includeDeleteCol={isEditingMode && !isCurrencyMismatch}
        newRowData={{
          currency: selectedCurrency,
          limitType: LayerResponseDtoLimitTypeEnum.Primary,
          limit: 0,
          layerPremium: 0,
          totalDeductions: 10,
          shareOfLimitQuoted: 0,
          lineSize: 100,
          rateAdequacy: '0',
        }}
        validateRow={validateLayer}
        onDeleteRow={onDeleteRow}
        onDataChange={onDataChange}
        onErrorChange={setHasError}
      />

      {isCurrencyMismatch && (
        <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-4 py-3 mb-4 rounded">
          {t('layerStructure.currencyMismatchWarning', {
            currency: submission.originalCurrency,
          })}
        </div>
      )}

      <FormActionButtons
        confirmText="Next"
        cancelText="Back"
        isConfirmDisabled={
          createLayersMutation.isLoading ||
          hasError.strict ||
          rowData.length === 0
        }
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
        onCancel={handleNavigateBack}
        onConfirm={handleNavigateNext}
      />
    </>
  );
};
